/**
 * <g-table> - Data Table with Sort + Pagination
 *
 * Renders a sortable, paginated table from a JS data array. Light DOM —
 * the host can style cells freely with Tailwind/DaisyUI. Filtering and
 * data fetching stay in the host (Alpine component); this element owns
 * rendering + sort + paginate + row-click.
 *
 * @example
 *   <g-table id="t" page-size="20" empty-text="No accounts found"></g-table>
 *   <script>
 *     const t = document.getElementById('t');
 *     t.columns = [
 *       { field: 'Customer Name', label: 'Customer', sortable: true, classes: 'font-medium' },
 *       { field: 'Original Balance', label: 'Original',
 *         render: 'currency', currency: 'USD', sortable: true },
 *       { field: 'Status', label: 'Status',
 *         render: 'badge', colorMap: { Active: 'success', Pending: 'warning', Closed: 'neutral' } },
 *       { field: 'Last Payment Date', label: 'Last Payment', render: 'date', sortable: true }
 *     ];
 *     t.data = accounts;
 *     t.addEventListener('row-click', (e) => openDetail(e.detail.row));
 *   </script>
 *
 * Properties (set via JS, not attributes):
 *   .columns  Array<ColumnDef> — see field shape below
 *   .data     Array<object>    — row records (keys match column.field exactly)
 *
 * Attributes:
 *   page-size    integer (default 20)
 *   empty-text   string  (default "No records found")
 *   loading      boolean (presence = show spinner row)
 *
 * ColumnDef:
 *   { field: string,                     // exact key into row
 *     label?: string,                    // header text (defaults to field)
 *     sortable?: boolean,                // default false
 *     classes?: string,                  // extra <td> classes
 *     render?: 'text'|'currency'|'date'|'datetime'|'number'|'boolean'|'badge'|'ref-link',
 *     currency?: string,                 // for render:'currency' (default 'USD')
 *     colorMap?: Record<string,string>,  // for render:'badge' — value → daisy color
 *     displayField?: string              // for render:'ref-link' — key on populated ref
 *   }
 *
 * Events:
 *   row-click    detail: { row, index }
 *   sort-change  detail: { field, direction }
 *   page-change  detail: { page, totalPages }
 */

// Boolean cell: green check for truthy, red x for falsy. Null / undefined /
// empty string → em-dash (no answer yet, distinct from "answered no").
//
// Uses inline SVG instead of `<i class="fa-...">` because Font Awesome's
// MutationObserver doesn't reliably catch icons inserted deep inside an
// innerHTML replace cycle — the same render path the table runs on every
// data refresh and pagination change. SVG renders unconditionally, has no
// kit/network dependency, and inherits currentColor from the surrounding
// utility class so daisy semantic colors still apply.
const SVG_CHECK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M9 12l2 2 4-4"></path></svg>`;
const SVG_XMARK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"></circle><path d="M9 9l6 6"></path><path d="M15 9l-6 6"></path></svg>`;

const renderBoolean = (v) => {
  if (v == null || v === '') return '—';
  // Treat string forms ("true" / "false") the same as real booleans —
  // upstream sometimes serializes via JSON.stringify before reaching the
  // table (e.g., when piped through a generic text renderer fallback).
  const truthy = v === true || v === 1 || v === '1' || v === 'true' || v === 'yes';
  const falsy = v === false || v === 0 || v === '0' || v === 'false' || v === 'no';
  if (!truthy && !falsy) return escapeHtml(String(v));
  return truthy
    ? `<span class="text-success inline-flex" title="Yes" aria-label="Yes">${SVG_CHECK}</span>`
    : `<span class="text-error inline-flex" title="No" aria-label="No">${SVG_XMARK}</span>`;
};

const RENDERERS = {
  text: (v) => {
    // Auto-promote raw booleans to the icon renderer so columns that didn't
    // get tagged with render:'boolean' still display correctly. Strings
    // "true" / "false" are NOT promoted here — they may legitimately be
    // labels for something else; opt-in via render:'boolean'.
    if (v === true || v === false) return renderBoolean(v);
    return v == null || v === '' ? '—' : escapeHtml(String(v));
  },
  number: (v) => v == null || v === '' ? '—' : escapeHtml(String(v)),
  boolean: renderBoolean,
  currency: (v, col) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '—';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: col.currency || 'USD' }).format(n);
    } catch { return String(v); }
  },
  date: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return escapeHtml(String(v));
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  },
  datetime: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return escapeHtml(String(v));
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  },
  badge: (v, col) => {
    if (v == null || v === '') return '—';
    const map = col.colorMap || {};
    const colorKey = map[v] || 'neutral';
    const cls = `badge badge-soft badge-sm badge-${colorKey}`;
    return `<span class="${cls}">${escapeHtml(String(v))}</span>`;
  },
  'ref-link': (v, col) => {
    if (!v) return '—';
    const display = (typeof v === 'object' && v) ? v[col.displayField || 'name'] : v;
    return escapeHtml(String(display ?? '—'));
  },
};

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function renderCell(row, col) {
  const raw = row[col.field];
  const fn = RENDERERS[col.render] || RENDERERS.text;
  return fn(raw, col);
}

export class GTable extends HTMLElement {
  constructor() {
    super();
    this._columns = [];
    this._data = [];
    this._sort = { field: null, direction: 'asc' }; // persists across .data refresh
    this._page = 1;
    this._renderScheduled = false;
  }

  static get observedAttributes() {
    return ['page-size', 'empty-text', 'loading'];
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  connectedCallback() {
    this.classList.add('block');

    // Guard against double-attach. <g-tabs> reparents its children into a
    // panel-host wrapper, which triggers disconnect → connect on every
    // nested custom element. Without this guard, every reattach left a
    // stale `_root` div in the DOM showing whatever empty/loading state
    // was current at first attach (e.g. "No X found" + "No results"
    // pagination) while a fresh _root rendered the real data — both visible.
    if (!this._root || this._root.parentNode !== this) {
      // Drop any stale root divs from a prior attach cycle.
      [...this.children].forEach(c => {
        if (c.dataset && c.dataset.gtableRoot === '') c.remove();
      });
      this._root = document.createElement('div');
      this._root.dataset.gtableRoot = '';
      this.appendChild(this._root);
    }

    this.scheduleRender();
    this.addEventListener('click', this._handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
  }

  // ── Public API ──

  get columns() { return this._columns; }
  set columns(cols) {
    this._columns = Array.isArray(cols) ? cols : [];
    this.scheduleRender();
  }

  get data() { return this._data; }
  set data(rows) {
    this._data = Array.isArray(rows) ? rows : [];
    // Clamp page if data shrank
    const total = Math.max(1, Math.ceil(this._data.length / this.pageSize));
    if (this._page > total) this._page = total;
    this.scheduleRender();
  }

  get pageSize() {
    const n = parseInt(this.getAttribute('page-size'), 10);
    return Number.isFinite(n) && n > 0 ? n : 20;
  }

  get emptyText() {
    return this.getAttribute('empty-text') || 'No records found';
  }

  get loading() {
    return this.hasAttribute('loading');
  }

  setSort(field, direction) {
    this._sort = { field, direction: direction === 'desc' ? 'desc' : 'asc' };
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('sort-change', {
      detail: { ...this._sort }, bubbles: true, composed: true,
    }));
  }

  setPage(page) {
    const totalPages = this._totalPages();
    const next = Math.max(1, Math.min(page, totalPages));
    if (next === this._page) return;
    this._page = next;
    this.scheduleRender();
    this.dispatchEvent(new CustomEvent('page-change', {
      detail: { page: this._page, totalPages }, bubbles: true, composed: true,
    }));
  }

  // ── Internals ──

  _totalPages() {
    return Math.max(1, Math.ceil(this._data.length / this.pageSize));
  }

  _sortedData() {
    if (!this._sort.field) return this._data;
    const { field, direction } = this._sort;
    const dir = direction === 'desc' ? -1 : 1;
    // Stable-ish sort. Comparing typed: numbers, dates, then strings.
    const copy = this._data.slice();
    copy.sort((a, b) => {
      const av = a?.[field];
      const bv = b?.[field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      // Number compare
      const an = typeof av === 'number' ? av : parseFloat(av);
      const bn = typeof bv === 'number' ? bv : parseFloat(bv);
      if (!isNaN(an) && !isNaN(bn) && (typeof av !== 'string' || /^\s*[\d.,-]+\s*$/.test(av))) {
        return (an - bn) * dir;
      }
      // Date compare
      const ad = Date.parse(av);
      const bd = Date.parse(bv);
      if (!isNaN(ad) && !isNaN(bd) && typeof av === 'string' && /\d{4}-\d{2}-\d{2}|\//.test(av)) {
        return (ad - bd) * dir;
      }
      // String fallback
      return String(av).localeCompare(String(bv)) * dir;
    });
    return copy;
  }

  _paginated(sorted) {
    const start = (this._page - 1) * this.pageSize;
    return sorted.slice(start, start + this.pageSize);
  }

  scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._render();
    });
  }

  _render() {
    if (!this._root) return;
    const cols = this._columns;
    const colspan = Math.max(1, cols.length);
    const sorted = this._sortedData();
    const totalPages = this._totalPages();
    const pageRows = this._paginated(sorted);
    const total = this._data.length;
    const pageStart = total === 0 ? 0 : (this._page - 1) * this.pageSize;
    const pageEnd = Math.min(pageStart + this.pageSize, total);

    const thead = `<thead><tr>${cols.map(c => this._renderHeader(c)).join('')}</tr></thead>`;

    let tbodyRows;
    if (this.loading) {
      tbodyRows = `<tr><td colspan="${colspan}" class="text-center py-8"><span class="loading loading-spinner loading-md"></span></td></tr>`;
    } else if (pageRows.length === 0) {
      tbodyRows = `<tr><td colspan="${colspan}" class="text-center py-10 text-base-content/50"><i class="fa-regular fa-inbox text-2xl mb-2 block"></i>${escapeHtml(this.emptyText)}</td></tr>`;
    } else {
      tbodyRows = pageRows.map((row, i) => {
        const absoluteIndex = pageStart + i;
        const cells = cols.map(c => {
          const cls = c.classes ? ` class="${escapeHtml(c.classes)}"` : '';
          return `<td${cls}>${renderCell(row, c)}</td>`;
        }).join('');
        return `<tr class="hover cursor-pointer" data-row-index="${absoluteIndex}">${cells}</tr>`;
      }).join('');
    }
    const tbody = `<tbody>${tbodyRows}</tbody>`;

    const pagination = this._renderPagination(total, pageStart, pageEnd, totalPages);

    this._root.innerHTML = `
      <div class="overflow-auto">
        <table class="table table-sm">
          ${thead}
          ${tbody}
        </table>
      </div>
      ${pagination}
    `;
  }

  _renderHeader(col) {
    const label = escapeHtml(col.label || col.field);
    if (!col.sortable) return `<th>${label}</th>`;
    const isActive = this._sort.field === col.field;
    const arrow = isActive ? (this._sort.direction === 'desc' ? ' ↓' : ' ↑') : '';
    return `<th class="cursor-pointer select-none" data-sort-field="${escapeHtml(col.field)}"${isActive ? ` aria-sort="${this._sort.direction === 'desc' ? 'descending' : 'ascending'}"` : ''}>${label}<span class="opacity-60">${arrow}</span></th>`;
  }

  _renderPagination(total, pageStart, pageEnd, totalPages) {
    const range = total > 0 ? `Showing ${pageStart + 1}–${pageEnd} of ${total}` : 'No results';
    return `
      <div class="border-t border-base-300 px-5 py-3 flex justify-between items-center">
        <span class="text-sm text-base-content/60 truncate">${escapeHtml(range)}</span>
        <div class="join">
          <button class="join-item btn btn-sm" data-page="prev"${this._page <= 1 ? ' disabled' : ''}>«</button>
          <button class="join-item btn btn-sm">${this._page}</button>
          <button class="join-item btn btn-sm" data-page="next"${this._page >= totalPages ? ' disabled' : ''}>»</button>
        </div>
      </div>
    `;
  }

  _handleClick = (e) => {
    const sortHeader = e.target.closest('[data-sort-field]');
    if (sortHeader && this.contains(sortHeader)) {
      const field = sortHeader.getAttribute('data-sort-field');
      const nextDir = (this._sort.field === field && this._sort.direction === 'asc') ? 'desc' : 'asc';
      this.setSort(field, nextDir);
      return;
    }
    const pageBtn = e.target.closest('[data-page]');
    if (pageBtn && this.contains(pageBtn)) {
      const dir = pageBtn.getAttribute('data-page');
      this.setPage(this._page + (dir === 'next' ? 1 : -1));
      return;
    }
    const row = e.target.closest('tr[data-row-index]');
    if (row && this.contains(row)) {
      // Don't fire row-click on interactive children
      if (e.target.closest('button, a, input, select, textarea, label')) return;
      const idx = parseInt(row.getAttribute('data-row-index'), 10);
      const sorted = this._sortedData();
      const item = sorted[idx];
      if (item) {
        this.dispatchEvent(new CustomEvent('row-click', {
          detail: { row: item, index: idx },
          bubbles: true,
          composed: true,
        }));
      }
    }
  };
}

if (!customElements.get('g-table')) {
  customElements.define('g-table', GTable);
}
